import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, CreditCard, Palette, Sun, Moon, Check, Wallet } from 'lucide-react';
import { useAuth } from '../../stores/AuthContext';
import { useTheme } from '../../stores/ThemeContext';
import { getGoals, createGoal, getDashboardAnalytics } from '../../services/dashboardAnalytics';
import { TIMEZONE_OPTIONS, getSessionScheduleForTimezone, SESSION_COLORS } from '../../utils/tradingSessions';
import { BillingSection } from '../billing/BillingPage';

const TABS = [
  { id: 'account', label: 'Account', icon: User },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'appearance', label: 'Appearance', icon: Palette },
];

const SectionCard = ({ title, description, children }) => (
  <div className="bg-card border border-border rounded-xl p-6">
    <div className="mb-5">
      <h2 className="text-base font-semibold text-foreground">{title}</h2>
      {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
    </div>
    {children}
  </div>
);

const FormField = ({ label, helperText, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-foreground">{label}</label>
    {children}
    {helperText && <p className="text-xs text-muted-foreground">{helperText}</p>}
  </div>
);

const inputClass =
  'w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground';

const Profile = () => {
  const { user, updateProfile, updatePassword, deleteAccount } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return TABS.find(t => t.id === tab) ? tab : 'account';
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [goalsLoading, setGoalsLoading] = useState(false);

  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleteExpanded, setDeleteExpanded] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [profileData, setProfileData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    timezone: user?.timezone || 'UTC',
  });

  const [balanceData, setBalanceData] = useState({
    starting_balance: user?.starting_balance || '',
  });
  const [balanceLoading, setBalanceLoading] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [goalsData, setGoalsData] = useState({
    monthly_profit: '',
    reduce_emotional: '',
    journal_consistency: '',
  });

  useEffect(() => {
    const loadGoals = async () => {
      try {
        const result = await getGoals();
        if (result.success && result.goals) {
          const goalsMap = {};
          for (const goal of result.goals) {
            goalsMap[goal.goal_type] = String(goal.target_value);
          }
          setGoalsData(prev => ({ ...prev, ...goalsMap }));
        }
      } catch (err) {
        console.error('Failed to load goals:', err);
      }
    };
    loadGoals();
  }, []);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
    setError('');
    setSuccess('');
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      await updateProfile(profileData);
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleBalanceSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      setBalanceLoading(true);
      await updateProfile({ starting_balance: Number(balanceData.starting_balance) });
      await getDashboardAnalytics(true).catch(() => {});
      setSuccess('Starting balance updated successfully');
    } catch (err) {
      setError('Failed to update starting balance');
    } finally {
      setBalanceLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return setError('Passwords do not match');
    }
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      await updatePassword(passwordData.currentPassword, passwordData.newPassword);
      setSuccess('Password updated successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError('Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (deleteConfirmEmail !== user?.email) return;
    try {
      setDeleteLoading(true);
      setError('');
      await deleteAccount();
      // deleteAccount clears auth state; navigate to login
      window.location.href = '/login';
    } catch (err) {
      setError('Failed to delete account. Please try again.');
      setDeleteLoading(false);
    }
  };

  const handleGoalsSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      setGoalsLoading(true);
      const goalEntries = [
        { goal_type: 'monthly_profit', target_value: goalsData.monthly_profit },
        { goal_type: 'reduce_emotional', target_value: goalsData.reduce_emotional },
        { goal_type: 'journal_consistency', target_value: goalsData.journal_consistency },
      ].filter(g => g.target_value !== '' && Number(g.target_value) >= 0);

      for (const goal of goalEntries) {
        await createGoal({ goal_type: goal.goal_type, target_value: Number(goal.target_value) });
      }
      await getDashboardAnalytics(true).catch(() => {});
      setSuccess('Trading goals updated successfully');
    } catch (err) {
      setError('Failed to update trading goals');
    } finally {
      setGoalsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-foreground mb-6">Settings</h1>

        {/* Tab Bar */}
        <div className="flex gap-1 bg-card border border-border rounded-xl p-1 mb-6 w-fit">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="space-y-6">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>
            )}
            {success && (
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm">{success}</div>
            )}

            {/* Profile Information */}
            <SectionCard title="Profile Information">
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Username">
                    <input
                      className={inputClass}
                      name="username"
                      value={profileData.username}
                      onChange={e => setProfileData(p => ({ ...p, username: e.target.value }))}
                    />
                  </FormField>
                  <FormField label="Email">
                    <input
                      className={inputClass}
                      type="email"
                      name="email"
                      value={profileData.email}
                      onChange={e => setProfileData(p => ({ ...p, email: e.target.value }))}
                    />
                  </FormField>
                </div>
                <FormField
                  label="Timezone"
                  helperText="Used to determine trading session times (London, New York, Asia, Sydney)"
                >
                  <select
                    className={inputClass}
                    name="timezone"
                    value={profileData.timezone}
                    onChange={e => setProfileData(p => ({ ...p, timezone: e.target.value }))}
                  >
                    {TIMEZONE_OPTIONS.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </FormField>

                {profileData.timezone && (
                  <div className="p-3 bg-secondary rounded-lg">
                    <p className="text-xs font-medium text-foreground mb-2">Trading Sessions in Your Timezone</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(getSessionScheduleForTimezone(profileData.timezone)).map(([name, info]) => (
                        <div key={name} className="flex items-center gap-1.5 px-2 py-1 bg-card border border-border rounded-md">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: SESSION_COLORS[name] }} />
                          <span className="text-xs font-semibold text-foreground">{name}:</span>
                          <span className="text-xs text-muted-foreground">
                            {String(info.localStart).padStart(2, '0')}:00 – {String(info.localEnd).padStart(2, '0')}:00
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Update Profile'}
                </button>
              </form>
            </SectionCard>

            {/* Account Balance */}
            <SectionCard
              title="Account Balance"
              description="Set your starting account balance to enable equity tracking and risk metrics on your Dashboard."
            >
              <form onSubmit={handleBalanceSubmit} className="space-y-4">
                <FormField label="Starting Balance" helperText="Your account balance when you started tracking trades">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <input
                      className={`${inputClass} pl-7`}
                      type="number"
                      step="0.01"
                      min="0"
                      name="starting_balance"
                      placeholder="e.g. 10000"
                      value={balanceData.starting_balance}
                      onChange={e => setBalanceData({ starting_balance: e.target.value })}
                    />
                  </div>
                </FormField>
                <button
                  type="submit"
                  disabled={balanceLoading}
                  className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {balanceLoading ? 'Saving...' : 'Update Balance'}
                </button>
              </form>
            </SectionCard>

            {/* Change Password */}
            <SectionCard title="Change Password">
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <FormField label="Current Password">
                  <input
                    className={inputClass}
                    type="password"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={e => setPasswordData(p => ({ ...p, currentPassword: e.target.value }))}
                    required
                  />
                </FormField>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="New Password">
                    <input
                      className={inputClass}
                      type="password"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={e => setPasswordData(p => ({ ...p, newPassword: e.target.value }))}
                      required
                    />
                  </FormField>
                  <FormField label="Confirm New Password">
                    <input
                      className={inputClass}
                      type="password"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={e => setPasswordData(p => ({ ...p, confirmPassword: e.target.value }))}
                      required
                    />
                  </FormField>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Update Password'}
                </button>
              </form>
            </SectionCard>

            {/* Trading Goals */}
            <SectionCard
              title="Trading Goals"
              description="Set your monthly trading goals. Progress will be displayed on your Dashboard."
            >
              <form onSubmit={handleGoalsSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField label="Monthly Profit Target" helperText="Your target profit for the month">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <input
                        className={`${inputClass} pl-7`}
                        type="number"
                        name="monthly_profit"
                        value={goalsData.monthly_profit}
                        onChange={e => setGoalsData(p => ({ ...p, monthly_profit: e.target.value }))}
                      />
                    </div>
                  </FormField>
                  <FormField label="Max Emotional Trades" helperText="Maximum emotional trades allowed per month">
                    <input
                      className={inputClass}
                      type="number"
                      name="reduce_emotional"
                      value={goalsData.reduce_emotional}
                      onChange={e => setGoalsData(p => ({ ...p, reduce_emotional: e.target.value }))}
                    />
                  </FormField>
                  <FormField label="Journal Consistency" helperText="Target days of journaling per month">
                    <div className="relative">
                      <input
                        className={`${inputClass} pr-12`}
                        type="number"
                        name="journal_consistency"
                        value={goalsData.journal_consistency}
                        onChange={e => setGoalsData(p => ({ ...p, journal_consistency: e.target.value }))}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">days</span>
                    </div>
                  </FormField>
                </div>
                <button
                  type="submit"
                  disabled={goalsLoading}
                  className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {goalsLoading ? 'Saving...' : 'Update Goals'}
                </button>
              </form>
            </SectionCard>
            {/* Danger Zone */}
            <div className="border border-red-500/30 rounded-xl p-6">
              <div className="mb-5">
                <h2 className="text-base font-semibold text-red-400">Danger Zone</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
              </div>

              {!deleteExpanded ? (
                <button
                  type="button"
                  onClick={() => setDeleteExpanded(true)}
                  className="px-4 py-2 text-sm font-medium border border-red-500/50 text-red-400 rounded-lg hover:bg-red-500/10 transition-colors"
                >
                  Delete My Account
                </button>
              ) : (
                <form onSubmit={handleDeleteAccount} className="space-y-4">
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    This will permanently delete your account, all trades, journals, and cancel any active subscription.
                    Type your email address to confirm.
                  </div>
                  <FormField label="Confirm your email address">
                    <input
                      className={inputClass}
                      type="email"
                      value={deleteConfirmEmail}
                      onChange={e => setDeleteConfirmEmail(e.target.value)}
                      placeholder={user?.email}
                      autoComplete="off"
                    />
                  </FormField>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={deleteLoading || deleteConfirmEmail !== user?.email}
                      className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {deleteLoading ? 'Deleting...' : 'Permanently Delete Account'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setDeleteExpanded(false); setDeleteConfirmEmail(''); }}
                      className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && <BillingSection />}

        {/* Appearance Tab */}
        {activeTab === 'appearance' && (
          <div className="space-y-6">
            <SectionCard title="Theme" description="Choose your preferred color scheme for the application.">
              <div className="grid grid-cols-2 gap-4 max-w-sm">
                {/* Dark option */}
                <button
                  onClick={() => !isDark && toggleTheme()}
                  className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                    isDark
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/40'
                  }`}
                >
                  <div className="w-full h-16 rounded-lg bg-zinc-900 border border-zinc-700 flex items-center justify-center">
                    <Moon className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isDark && <Check className="w-3.5 h-3.5 text-primary" />}
                    <span className={`text-sm font-medium ${isDark ? 'text-foreground' : 'text-muted-foreground'}`}>
                      Dark
                    </span>
                  </div>
                </button>

                {/* Light option */}
                <button
                  onClick={() => isDark && toggleTheme()}
                  className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                    !isDark
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/40'
                  }`}
                >
                  <div className="w-full h-16 rounded-lg bg-zinc-100 border border-zinc-300 flex items-center justify-center">
                    <Sun className="w-5 h-5 text-zinc-500" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    {!isDark && <Check className="w-3.5 h-3.5 text-primary" />}
                    <span className={`text-sm font-medium ${!isDark ? 'text-foreground' : 'text-muted-foreground'}`}>
                      Light
                    </span>
                  </div>
                </button>
              </div>
            </SectionCard>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
